import { createFileRoute, redirect } from '@tanstack/react-router'
import {
  BOT_DETECTION_PREDEFINED_DATE_RANGE_IDS,
  botDetectionArraySearchParam,
  botDetectionBooleanSearchParam,
  botDetectionCategorySearchParamSchema,
  botDetectionConfidenceSearchParamSchema,
  BotDetectionEventsPage,
  botDetectionIdentitySearchParamSchema,
  botDetectionSourceSearchParamSchema,
} from 'features/botDetection'
import { withCachedSearch } from 'helpers/withCachedSearch'
import { z } from 'zod/v4'

const botDetectionEventsSearchSchema = z.object({
  since: z.string().optional(),
  before: z.string().optional(),
  aggregateRange: botDetectionBooleanSearchParam,
  period: z.enum(BOT_DETECTION_PREDEFINED_DATE_RANGE_IDS).optional().catch(undefined),
  limit: z.coerce.number().optional(),
  botDetected: botDetectionBooleanSearchParam,
  botName: botDetectionArraySearchParam(z.string()),
  category: botDetectionArraySearchParam(botDetectionCategorySearchParamSchema),
  identity: botDetectionArraySearchParam(botDetectionIdentitySearchParamSchema),
  confidence: botDetectionArraySearchParam(botDetectionConfidenceSearchParamSchema),
  provider: botDetectionArraySearchParam(z.string()),
  ipAddress: z.string().optional(),
  proxy: botDetectionBooleanSearchParam,
  vpn: botDetectionBooleanSearchParam,
  environmentIds: botDetectionArraySearchParam(z.string()),
  source: botDetectionSourceSearchParamSchema.optional().catch(undefined),
})

const botDetectionCachedSearchParams = [
  'period',
  'before',
  'since',
  'botDetected',
  'botName',
  'category',
  'provider',
  'identity',
  'confidence',
  'proxy',
  'vpn',
  'environmentIds',
  'source',
] as const

export type BotDetectionEventsSearchParams = z.infer<typeof botDetectionEventsSearchSchema>
type BotDetectionCachedSearchParam = (typeof botDetectionCachedSearchParams)[number]
type BotDetectionCachedSearch = Pick<BotDetectionEventsSearchParams, BotDetectionCachedSearchParam>

const getBotDetectionCachedSearchRoute = (subscriptionId: string) => `/workspaces/${subscriptionId}/bot-detection`

export const Route = createFileRoute('/_authenticated/_layout/workspaces/$subscriptionId/bot-detection/events/')({
  validateSearch: botDetectionEventsSearchSchema,
  component: BotDetectionEventsPage,
  beforeLoad: ({ search, location, params, cause }) => {
    if (cause === 'preload') {
      return
    }

    const cachedSearch = withCachedSearch<BotDetectionCachedSearch>({
      search,
      matchedRoute: getBotDetectionCachedSearchRoute(params.subscriptionId),
      cachedParamsWhitelist: botDetectionCachedSearchParams,
      searchStr: location.searchStr,
    })

    if (cachedSearch) {
      throw redirect({
        to: '/workspaces/$subscriptionId/bot-detection/events',
        params,
        replace: true,
        search: (old) => ({
          ...old,
          ...cachedSearch,
        }),
      })
    }
  },
})
